import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useChallengeDetail } from '../hooks/useChallengeDetail'
import { useJoin, parseJoinError } from '../hooks/useJoin'
import { useRefund, parseRefundError } from '../hooks/useRefund'
import { useWallet } from '../contexts/WalletContext'
import { signInWithEthereum } from '../lib/auth'
import {
  getChallengeStatus,
  getChallengeStatusLabel,
  getChallengeTypeName,
  formatWeiToEth,
  formatAddress,
  formatTimestamp,
  getCurrentRound,
  CHALLENGE_STATE,
  CHALLENGE_TYPE,
} from '../types/challenge'

const etherscanBase = 'https://sepolia.etherscan.io'
const ADMIN_ADDRESS = (import.meta.env.VITE_ADMIN_ADDRESS as string | undefined)?.toLowerCase()

function statusBadgeClass(s: ReturnType<typeof getChallengeStatus>) {
  switch (s) {
    case 'active':
      return 'badge badge-success'
    case 'upcoming':
      return 'badge badge-neutral'
    case 'ended':
      return 'badge badge-ink'
    case 'refunded':
      return 'badge badge-warning'
  }
}

export default function ChallengeDetail() {
  const { address } = useParams<{ address: string }>()
  const { address: userAddress, isConnected, openConnectModal } = useWallet()
  const navigate = useNavigate()
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const { data: challenge, isLoading, isError } = useChallengeDetail(address ?? '', userAddress)
  const { mutate: join, isPending: isJoining } = useJoin(address ?? '', challenge?.entryFee ?? 0n)
  const { mutate: refund, isPending: isRefunding } = useRefund(address ?? '')

  const isAdmin = Boolean(
    userAddress && ADMIN_ADDRESS && userAddress.toLowerCase() === ADMIN_ADDRESS,
  )

  async function handleDelete() {
    if (!address) return
    if (
      !window.confirm(
        '이 챌린지를 플랫폼 목록에서 숨깁니다.\n(온체인 컨트랙트는 블록체인에 그대로 남습니다.)\n계속할까요?',
      )
    )
      return
    setIsDeleting(true)
    try {
      const jwt = await signInWithEthereum()
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
      const resp = await fetch(`${apiBase}/api/admin/challenges/${address}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ hidden: true }),
      })
      if (!resp.ok) {
        const err = (await resp.json().catch(() => ({}))) as { error?: string }
        throw new Error(err.error ?? '삭제에 실패했습니다')
      }
      navigate('/')
    } catch (e) {
      setToast({ type: 'error', message: e instanceof Error ? e.message : '삭제에 실패했습니다' })
      setTimeout(() => setToast(null), 4000)
    } finally {
      setIsDeleting(false)
    }
  }

  function handleRefund() {
    if (!isConnected) {
      openConnectModal()
      return
    }
    refund(undefined, {
      onSuccess: () => {
        setToast({ type: 'success', message: '참가비를 환불받았습니다.' })
        setTimeout(() => setToast(null), 4000)
      },
      onError: (err) => {
        setToast({ type: 'error', message: parseRefundError(err) })
        setTimeout(() => setToast(null), 5000)
      },
    })
  }

  function handleJoin() {
    if (!isConnected) {
      openConnectModal()
      return
    }
    join(undefined, {
      onSuccess: () => {
        setToast({ type: 'success', message: '참가가 완료되었습니다!' })
        setTimeout(() => setToast(null), 4000)
      },
      onError: (err) => {
        setToast({ type: 'error', message: parseJoinError(err) })
        setTimeout(() => setToast(null), 5000)
      },
    })
  }

  if (!address)
    return (
      <div className="empty">
        <p>잘못된 챌린지 주소입니다.</p>
      </div>
    )

  if (isLoading)
    return (
      <div className="empty">
        <div className="spinner spinner-lg" />
        <p>챌린지 정보를 불러오는 중…</p>
      </div>
    )

  if (isError || !challenge)
    return (
      <div className="empty">
        <span className="empty-icon">⚠️</span>
        <p>챌린지 정보를 불러오지 못했습니다.</p>
        <button className="btn btn-ghost btn-sm" onClick={() => void navigate(-1)}>
          뒤로 가기
        </button>
      </div>
    )

  const status = getChallengeStatus(challenge)
  const currentRound = getCurrentRound(challenge.roundDeadlines)
  const roundCount = challenge.roundDeadlines.length
  // Joining is allowed while the challenge is PENDING (not yet activated), matching the
  // contract — the on-chain join() only requires PENDING state, not a time window.
  const canJoin =
    challenge.state === CHALLENGE_STATE.PENDING &&
    !challenge.isParticipant &&
    challenge.participantCount < challenge.maxParticipants
  // FAILED (under-min) entry-fee participants can reclaim their entry fee.
  const isFailed = challenge.state === CHALLENGE_STATE.REFUNDED
  const canRefund = isFailed && challenge.isParticipant && !challenge.isRefunded
  const pct =
    challenge.maxParticipants > 0
      ? Math.min(100, Math.round((challenge.participantCount / challenge.maxParticipants) * 100))
      : 0

  return (
    <div className="page-narrow">
      <Link to="/" className="btn btn-quiet btn-sm" style={{ marginBottom: 20, paddingLeft: 0 }}>
        ← 목록으로
      </Link>

      {/* Header card */}
      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="detail-head">
          <h1 className="detail-title">{challenge.name || address}</h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <span className={statusBadgeClass(status)} style={{ fontSize: 12.5 }}>
              {getChallengeStatusLabel(status)}
            </span>
            <span className="badge badge-primary" style={{ fontSize: 12.5 }}>
              {getChallengeTypeName(challenge.challengeType)}
            </span>
          </div>
          {challenge.description && <p className="detail-desc">{challenge.description}</p>}
        </div>
        <div className="info-rows">
          <div className="info-row">
            <span className="k">개설자</span>
            <a
              href={`${etherscanBase}/address/${challenge.creator}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)', fontSize: 13 }}
            >
              {formatAddress(challenge.creator)}
            </a>
          </div>
          <div className="info-row">
            <span className="k">기간</span>
            <span className="v">
              {formatTimestamp(challenge.startTime)} ~ {formatTimestamp(challenge.endTime)}
            </span>
          </div>
          {challenge.challengeType === CHALLENGE_TYPE.ENTRY_FEE ? (
            <div className="info-row">
              <span className="k">참가비</span>
              <span className="v eth-amt">{formatWeiToEth(challenge.entryFee)} ETH</span>
            </div>
          ) : (
            <div className="info-row">
              <span className="k">경품</span>
              <span className="v eth-amt">
                {challenge.challengeType === CHALLENGE_TYPE.PROMO_TOKEN
                  ? `${formatWeiToEth(challenge.entryFee)} ETH`
                  : '실물 경품'}
              </span>
            </div>
          )}
          <div className="info-row">
            <span className="k">참가자</span>
            <span className="v">
              {challenge.participantCount} / {challenge.maxParticipants}명
            </span>
          </div>
        </div>
        {/* Participation meter */}
        <div style={{ marginTop: 14 }}>
          <div className="meter">
            <i style={{ width: `${pct}%` }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 5, textAlign: 'right' }}>
            {pct}% 참가 완료
          </div>
        </div>
      </div>

      {/* Join card */}
      <div className="card card-pad" style={{ marginBottom: 16 }}>
        {canJoin && (
          <button
            className="btn btn-primary btn-lg btn-block"
            onClick={handleJoin}
            disabled={isJoining}
          >
            {isJoining ? (
              <>
                <span
                  className="spin"
                  style={{
                    display: 'inline-block',
                    width: 16,
                    height: 16,
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                  }}
                />{' '}
                처리 중…
              </>
            ) : isConnected ? (
              '참가하기'
            ) : (
              '지갑 연결 후 참가하기'
            )}
          </button>
        )}
        {challenge.isParticipant && challenge.state === CHALLENGE_STATE.PENDING && (
          <div className="alert alert-success">
            <span>✓</span> 참가 완료되었습니다.
          </div>
        )}
        {isFailed && challenge.isParticipant && (
          <>
            <div className="alert alert-warn" style={{ marginBottom: 12 }}>
              <span>ℹ️</span>
              <div>최소 인원 미달로 무산된 챌린지입니다. 참가비를 환불받을 수 있습니다.</div>
            </div>
            {canRefund ? (
              <button
                className="btn btn-primary btn-lg btn-block"
                onClick={handleRefund}
                disabled={isRefunding}
                data-testid="refund-btn"
              >
                {isRefunding
                  ? '처리 중…'
                  : `참가비 환불받기 (${formatWeiToEth(challenge.entryFee)} ETH)`}
              </button>
            ) : (
              <div className="alert alert-success">
                <span>✓</span> 환불 완료되었습니다.
              </div>
            )}
          </>
        )}
        {toast && (
          <div
            className={`alert ${toast.type === 'success' ? 'alert-success' : 'alert-danger'}`}
            style={{ marginTop: 12 }}
          >
            {toast.message}
          </div>
        )}

        {/* Submit proof shortcut */}
        {challenge.state === CHALLENGE_STATE.ACTIVE &&
          challenge.isParticipant &&
          currentRound < roundCount &&
          (challenge.currentRoundDone ? (
            <div className="alert alert-success" style={{ marginTop: 10 }}>
              <span>✓</span> {currentRound + 1}라운드 인증을 이미 완료했습니다.
            </div>
          ) : (
            <Link
              to={`/submit/${address}`}
              className="btn btn-primary btn-lg btn-block"
              style={{ marginTop: 10 }}
            >
              {currentRound + 1}라운드 인증하기
            </Link>
          ))}

        {/* Result link */}
        {challenge.state === CHALLENGE_STATE.ENDED && (
          <Link to={`/result/${address}`} className="btn btn-ghost btn-lg btn-block">
            정산 / 추첨 결과 보기
          </Link>
        )}
      </div>

      {/* Round summary */}
      {roundCount > 0 && (
        <div className="card card-pad">
          <div className="section-hd">
            <h2 className="section-title">인증 현황</h2>
            <Link to={`/gallery/${address}`} className="btn btn-quiet btn-sm">
              전체 인증 보기 →
            </Link>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {challenge.roundDeadlines.map((_, i) => {
              const isPast = i < currentRound
              const isCurrent = i === currentRound
              return (
                <span
                  key={i}
                  className={`badge ${isPast ? 'badge-neutral' : isCurrent ? 'badge-primary' : 'badge-neutral'} badge-lg`}
                >
                  {i + 1}라운드 {isCurrent ? '(진행 중)' : isPast ? '(완료)' : ''}
                </span>
              )
            })}
          </div>
        </div>
      )}

      {/* Admin: hide from platform (soft delete) */}
      {isAdmin && (
        <div
          className="card card-pad"
          style={{ marginTop: 16, border: '1px solid var(--danger-line)' }}
        >
          <div className="section-hd">
            <h2 className="section-title" style={{ color: 'var(--danger)' }}>
              관리자 영역
            </h2>
          </div>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '0 0 12px' }}>
            이 챌린지를 플랫폼 목록에서 숨깁니다. 블록체인에 기록된 온체인 컨트랙트는 삭제되지
            않으며, 목록에서만 보이지 않게 됩니다.
          </p>
          <button
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={isDeleting}
            data-testid="admin-delete-btn"
          >
            {isDeleting ? '처리 중…' : '챌린지 삭제 (목록에서 숨김)'}
          </button>
        </div>
      )}
    </div>
  )
}
